import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams, Navigate, Link } from 'react-router-dom';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { saveIntendedUrl } from '../../../utils/persistentNavigation';
import { SEO_DEFAULTS } from '../../../utils/seoConfig';
import { generateFAQSchema, generateBreadcrumbSchema, JsonLd } from '../../../utils/structuredData';
import { trackEvent } from '../../../utils/analytics';
import { useInMarketingShell } from '../../Marketing/MarketingContext';
import useReveal from '../useReveal';
import FurtherReading from '../FurtherReading';
import { COMPARE_CONFIGS, COMPARE_SLUGS } from './compareConfigs';
import '../AIEmployeesLandingPage.css';
import './ComparisonPage.css';

/**
 * ComparisonPage — ONE shared template for every /compare/{slug} page, driven
 * entirely by compareConfigs.js (same pattern as roles/RoleLandingPage; see
 * roles/README.md for the playbook — content lives in the config, this file is
 * structure only).
 *
 * AEO-first structure: answer paragraphs FIRST (what LLMs quote), then the
 * comparison table, honest per-tool verdicts, FAQ (FAQPage JSON-LD), CTA band.
 * All content is in the DOM without interaction; no animated numbers, so no
 * prerender-freeze logic is needed (the role pages' count-up trap does not
 * apply here). Reveal animations are progressive enhancement only.
 *
 * Theming follows RoleLandingPage exactly: dark-only in the logged-out
 * marketing shell; honors app theme when logged in.
 */

/** Renders a config prose string, converting markdown-style [text](/path)
 *  spans into real <a> anchors (internal-linking pass CEL-1309, Aug 2026).
 *  Keeps configs as plain strings; plain paragraphs pass through untouched.
 *  Real anchors (not Router Links) so crawlers follow them in the prerender. */
const renderLinked = (text) => {
  const re = /\[([^\]]+)\]\(([^)]+)\)/g;
  if (!re.test(text)) return text;
  re.lastIndex = 0;
  const parts = [];
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(<a key={`lnk-${i++}`} href={m[2]}>{m[1]}</a>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

const Section = ({ className = '', label, title, children }) => {
  const [ref, visible] = useReveal();
  return (
    <section className={`aie-section cmp-section ${className}`} ref={ref}>
      <div className={`aie-reveal ${visible ? 'is-visible' : ''}`}>
        {(label || title) && (
          <div className="aie-section-head cmp-section-head">
            {label && <span className="cmp-eyebrow">{label}</span>}
            {title && <h2 className="aie-section-title aie-display">{title}</h2>}
          </div>
        )}
        {children}
      </div>
    </section>
  );
};

const ComparisonPage = () => {
  const { compareSlug } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isDarkMode } = useTheme();
  const inMarketingShell = useInMarketingShell();

  const config = COMPARE_CONFIGS[compareSlug];
  if (!config) return <Navigate to="/compare/ai-employee-platforms" replace />;

  const dark = inMarketingShell ? true : isDarkMode;
  const canonicalUrl = `${SEO_DEFAULTS.url}/compare/${config.slug}`;

  const handleHire = (loc) => {
    trackEvent('cta_click', { cta_name: 'hire_ai_employee', compare_page: config.slug, location: loc });
    if (!currentUser) {
      saveIntendedUrl('/ai_employee');
      navigate('/signup');
      return;
    }
    navigate('/ai_employee');
  };

  const otherSlugs = COMPARE_SLUGS.filter((s) => s !== config.slug);

  // Per-page OG image when the config provides one; site default otherwise.
  const ogImage = config.seo.ogImage ? `${SEO_DEFAULTS.url}${config.seo.ogImage}` : SEO_DEFAULTS.image;

  return (
    <>
      <Helmet>
        <title>{config.seo.title}</title>
        <meta name="description" content={config.seo.description} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" type="text/markdown" href={`${canonicalUrl}/index.md`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={config.seo.title} />
        <meta property="og:description" content={config.seo.description} />
        <meta property="og:image" content={ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={config.seo.title} />
        <meta name="twitter:description" content={config.seo.description} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>
      <JsonLd data={generateFAQSchema(config.faq)} />
      <JsonLd
        data={generateBreadcrumbSchema([
          { name: 'Home', url: SEO_DEFAULTS.url },
          { name: 'AI Employees', url: `${SEO_DEFAULTS.url}/ai-employees` },
          { name: config.pageTitle, url: canonicalUrl },
        ])}
      />

      <div className={`aie-landing cmp-page ${dark ? 'dark' : 'light'}`}>
        {/* Hero — centered cinematic band, same visual language as the role pages
            (.rolp-hero): glow, breadcrumb, pill, display title, sub, CTAs. */}
        <section className="cmp-hero">
          <div className="cmp-hero__glow" aria-hidden="true" />
          <div className="cmp-hero__inner">
            <nav className="cmp-breadcrumb" aria-label="Breadcrumb">
              <Link to="/ai-employees">AI Employees</Link>
              <span aria-hidden="true">/</span>
              <span>{config.pageTitle}</span>
            </nav>
            <span className="cmp-hero__pill">
              <span className="cmp-hero__pill-dot" aria-hidden="true" />
              {config.hero.eyebrow}
            </span>
            <h1 className="aie-display cmp-hero__title">{config.hero.title}</h1>
            <p className="cmp-hero__sub">{config.hero.sub}</p>
            <div className="cmp-hero__ctas">
              <button type="button" className="aie-btn aie-btn-primary" onClick={() => handleHire('hero')}>
                Get started <ArrowRightOutlined />
              </button>
              <Link to="/ai-employees" className="aie-btn aie-btn-ghost">
                Explore roles
              </Link>
            </div>
            <p className="cmp-facts-asof">Facts checked as of {config.factsAsOf}.</p>
          </div>
        </section>

        {/* Answer-first summary — the block answer engines quote */}
        <Section className="cmp-answer-section">
          <div className="cmp-answer">
            {config.answerFirst.map((p, i) => (
              <p key={i} className="cmp-answer__p">{renderLinked(p)}</p>
            ))}
          </div>
        </Section>

        {/* Comparison table */}
        <Section label="Side by side" title="The comparison">
          <div className="cmp-table-wrap">
            {/* cmp-table--vs: first data column is CellCog (vs-layout) → teal spotlight.
                Category-layout tables (platform per row) get no column tint. */}
            <table className={`cmp-table ${config.table.columns[0] === '' ? 'cmp-table--vs' : ''}`}>
              <thead>
                <tr>
                  {config.table.columns.map((c, i) => (
                    <th key={i} scope="col">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {config.table.rows.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (j === 0
                      ? <th key={j} scope="row">{cell}</th>
                      : <td key={j}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {/* Honest verdicts */}
        <Section label="How to choose" title="Honest verdicts">
          <div className="cmp-verdicts">
            {config.verdicts.map((v, i) => (
              <div key={i} className="cmp-verdict">
                <h3 className="cmp-verdict__pick">{v.pick}</h3>
                <p className="cmp-verdict__when">{v.when}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* FAQ — content in the DOM via details (prerender-safe) */}
        <Section label="Questions" title="Frequently asked">
          <div className="cmp-faq">
            {config.faq.map((f, i) => (
              <details key={i} className="cmp-faq__item">
                <summary className="cmp-faq__q">{f.q}</summary>
                <p className="cmp-faq__a">{f.a}</p>
              </details>
            ))}
          </div>
          {/* Internal-link pass (Aug 2026): every compare page points at the
              self-organization proof surface with a descriptive anchor. */}
          <p className="cmp-faq__more">
            CellCog runs its own business on teams of AI employees.{' '}
            <Link to="/ai-organization" className="cmp-faq__link">
              See the live org chart of the AI organization that runs CellCog
            </Link>
            .
          </p>
        </Section>

        {/* Further reading — internal links into the blog cluster (2026-07-30).
            Opt-in per config: only pages that declare relatedPosts render it. */}
        {config.relatedPosts && (
          <Section label="Further reading" title="From the blog">
            <FurtherReading slugs={config.relatedPosts} />
          </Section>
        )}

        {/* Related comparisons */}
        <Section label="Keep comparing" title="More comparisons">
          <div className="cmp-related">
            {otherSlugs.map((s) => (
              <Link key={s} to={`/compare/${s}`} className="cmp-related__link">
                {COMPARE_CONFIGS[s].pageTitle}
                <ArrowRightOutlined />
              </Link>
            ))}
          </div>
        </Section>

        {/* CTA band */}
        <section className="cmp-cta-band">
          <h2 className="aie-display cmp-cta-band__title">{config.ctaBand.title}</h2>
          <p className="cmp-cta-band__body">{config.ctaBand.body}</p>
          <button type="button" className="cmp-cta-band__btn" onClick={() => handleHire('cta_band')}>
            Get started <ArrowRightOutlined />
          </button>
        </section>
      </div>
    </>
  );
};

export default ComparisonPage;
